/**
 * finance-service (:3004) public routes, relative to the centralized service URL config.
 *
 * Applications:
 * - `/finance/*` — FINANCE application **and** the live `finance.access` grant
 *   (FinanceModuleGuard). Finance & Accounts keys are employee grants that the
 *   Owner bypass does not cover.
 * - `/access/*` — FINANCE application; access administration forwarded to
 *   identity-service over a signed internal call (ACCESS-ADMINISTRATION.md).
 * - Company master, asset register, clients, invoices, vendor categories,
 *   files — HRMS **or** FINANCE, decided by role permissions.
 *
 * Source: `apps/finance-service/src/modules/**`.
 */
export const FINANCE = {
  // ------------------------------------------------- access administration
  access: {
    applications: '/access/applications',
    users: '/access/users',
    user: (userId: string) => `/access/users/${userId}`,
    userApplications: (userId: string) => `/access/users/${userId}/applications`,
    userApplication: (userId: string, application: string) =>
      `/access/users/${userId}/applications/${application}`,
    userCompanies: (userId: string) => `/access/users/${userId}/companies`,
    userRoles: (userId: string) => `/access/users/${userId}/roles`,
    roles: '/access/roles',
    assignableRoles: '/access/roles/assignable',
    role: (roleId: string) => `/access/roles/${roleId}`,
    rolePermissions: (roleId: string) => `/access/roles/${roleId}/permissions`,
    permissions: '/access/permissions',
    employeeAccount: (employeeId: string) => `/access/employees/${employeeId}/account`,
    employeeFinanceAccess: (employeeId: string) => `/access/employees/${employeeId}/finance-access`,
  },

  // --------------------------------------------------------- company master
  companies: {
    list: '/companies',
    company: (companyId: string) => `/companies/${companyId}`,
    restore: (companyId: string) => `/companies/${companyId}/restore`,
    history: (companyId: string) => `/companies/${companyId}/history`,
    addresses: (companyId: string) => `/companies/${companyId}/addresses`,
    address: (companyId: string, addressLinkId: string) =>
      `/companies/${companyId}/addresses/${addressLinkId}`,
    locations: (companyId: string) => `/companies/${companyId}/locations`,
    location: (companyId: string, locationId: string) =>
      `/companies/${companyId}/locations/${locationId}`,
    locationRestore: (companyId: string, locationId: string) =>
      `/companies/${companyId}/locations/${locationId}/restore`,
    locationAddresses: (companyId: string, locationId: string) =>
      `/companies/${companyId}/locations/${locationId}/addresses`,
    locationAddress: (companyId: string, locationId: string, addressLinkId: string) =>
      `/companies/${companyId}/locations/${locationId}/addresses/${addressLinkId}`,
    bankAccounts: (companyId: string) => `/companies/${companyId}/bank-accounts`,
    bankAccount: (companyId: string, bankAccountId: string) =>
      `/companies/${companyId}/bank-accounts/${bankAccountId}`,
    bankAccountSensitive: (companyId: string, bankAccountId: string) =>
      `/companies/${companyId}/bank-accounts/${bankAccountId}/sensitive`,
    bankAccountRestore: (companyId: string, bankAccountId: string) =>
      `/companies/${companyId}/bank-accounts/${bankAccountId}/restore`,
    cards: (companyId: string) => `/companies/${companyId}/cards`,
    card: (companyId: string, cardId: string) => `/companies/${companyId}/cards/${cardId}`,
    documents: (companyId: string) => `/companies/${companyId}/documents`,
    document: (companyId: string, documentId: string) =>
      `/companies/${companyId}/documents/${documentId}`,
    documentRestore: (companyId: string, documentId: string) =>
      `/companies/${companyId}/documents/${documentId}/restore`,
    /** Asset register (role permissions `assets.*`). */
    assets: (companyId: string) => `/companies/${companyId}/assets`,
    asset: (companyId: string, assetId: string) => `/companies/${companyId}/assets/${assetId}`,
    assetRestore: (companyId: string, assetId: string) =>
      `/companies/${companyId}/assets/${assetId}/restore`,
    assetAssignments: (companyId: string, assetId: string) =>
      `/companies/${companyId}/assets/${assetId}/assignments`,
    assetReturn: (companyId: string, assetId: string, assignmentId: string) =>
      `/companies/${companyId}/assets/${assetId}/assignments/${assignmentId}/return`,
  },
  assetCategories: {
    list: '/asset-categories',
    item: (id: string) => `/asset-categories/${id}`,
    restore: (id: string) => `/asset-categories/${id}/restore`,
  },
  vendorCategories: {
    list: '/vendor-categories',
    item: (id: string) => `/vendor-categories/${id}`,
    restore: (id: string) => `/vendor-categories/${id}/restore`,
  },
  /** An employee's assigned assets (role permission `employees.view`). */
  employeeAssets: (employeeId: string) => `/employees/${employeeId}/assets`,

  // ----------------------------------------------------------------- clients
  clients: {
    list: '/clients',
    client: (clientId: string) => `/clients/${clientId}`,
    restore: (clientId: string) => `/clients/${clientId}/restore`,
    staff: (clientId: string) => `/clients/${clientId}/staff`,
    staffMember: (clientId: string, staffId: string) => `/clients/${clientId}/staff/${staffId}`,
    staffRestore: (clientId: string, staffId: string) =>
      `/clients/${clientId}/staff/${staffId}/restore`,
    staffProjects: (clientId: string, staffId: string) =>
      `/clients/${clientId}/staff/${staffId}/projects`,
    staffProject: (clientId: string, staffId: string, linkId: string) =>
      `/clients/${clientId}/staff/${staffId}/projects/${linkId}`,
    addresses: (clientId: string) => `/clients/${clientId}/addresses`,
    address: (clientId: string, addressLinkId: string) =>
      `/clients/${clientId}/addresses/${addressLinkId}`,
    /** Clients linked to a project (project ids come from hrms-backend). */
    forProject: (projectId: string) => `/projects/${projectId}/clients`,
  },

  // --------------------------------------------------------------- invoicing
  invoicing: {
    invoices: '/invoices',
    invoiceSummary: '/invoices/summary',
    invoiceExport: '/invoices/export',
    invoice: (invoiceId: string) => `/invoices/${invoiceId}`,
    invoiceSend: (invoiceId: string) => `/invoices/${invoiceId}/send`,
    invoiceCancel: (invoiceId: string) => `/invoices/${invoiceId}/cancel`,
    invoiceDuplicate: (invoiceId: string) => `/invoices/${invoiceId}/duplicate`,
    payments: (invoiceId: string) => `/invoices/${invoiceId}/payments`,
    payment: (invoiceId: string, paymentId: string) =>
      `/invoices/${invoiceId}/payments/${paymentId}`,
    attachments: (invoiceId: string) => `/invoices/${invoiceId}/attachments`,
    attachment: (invoiceId: string, attachmentId: string) =>
      `/invoices/${invoiceId}/attachments/${attachmentId}`,
    numbering: (companyId: string) => `/companies/${companyId}/invoice-numbering`,
    numberingNext: (companyId: string) => `/companies/${companyId}/invoice-numbering/next`,
    items: (companyId: string) => `/companies/${companyId}/invoice-items`,
    item: (companyId: string, itemId: string) => `/companies/${companyId}/invoice-items/${itemId}`,
    itemArchive: (companyId: string, itemId: string) =>
      `/companies/${companyId}/invoice-items/${itemId}/archive`,
    itemRestore: (companyId: string, itemId: string) =>
      `/companies/${companyId}/invoice-items/${itemId}/restore`,
    taxes: (companyId: string) => `/companies/${companyId}/invoice-tax-schedules`,
    tax: (companyId: string, taxId: string) =>
      `/companies/${companyId}/invoice-tax-schedules/${taxId}`,
    taxActivate: (companyId: string, taxId: string) =>
      `/companies/${companyId}/invoice-tax-schedules/${taxId}/activate`,
    taxDeactivate: (companyId: string, taxId: string) =>
      `/companies/${companyId}/invoice-tax-schedules/${taxId}/deactivate`,
    templates: (companyId: string) => `/companies/${companyId}/invoice-templates`,
    template: (companyId: string, templateId: string) =>
      `/companies/${companyId}/invoice-templates/${templateId}`,
    templateDuplicate: (companyId: string, templateId: string) =>
      `/companies/${companyId}/invoice-templates/${templateId}/duplicate`,
    templateSetDefault: (companyId: string, templateId: string) =>
      `/companies/${companyId}/invoice-templates/${templateId}/set-default`,
    templateRestore: (companyId: string, templateId: string) =>
      `/companies/${companyId}/invoice-templates/${templateId}/restore`,
  },

  // ---------------------------------------- Finance & Accounts (/finance/*)
  finance: {
    activity: '/finance/activity',
    documents: '/finance/documents',
    document: (id: string) => `/finance/documents/${id}`,
    reminders: '/finance/reminders',
    remindersRun: '/finance/reminders/run',
    vendorOverview: '/finance/vendor-overview',
    assetOverview: '/finance/asset-overview',

    vendors: '/finance/vendors',
    vendor: (id: string) => `/finance/vendors/${id}`,
    vendorServices: '/finance/vendor-services',
    vendorService: (id: string) => `/finance/vendor-services/${id}`,
    vendorServiceBills: (id: string) => `/finance/vendor-services/${id}/bills`,

    expenses: '/finance/expenses',
    expense: (id: string) => `/finance/expenses/${id}`,
    expenseApprove: (id: string) => `/finance/expenses/${id}/approve`,
    expenseReject: (id: string) => `/finance/expenses/${id}/reject`,
    expensePayments: (expenseId: string) => `/finance/expenses/${expenseId}/payments`,
    expensePaymentVoid: (expenseId: string, paymentId: string) =>
      `/finance/expenses/${expenseId}/payments/${paymentId}/void`,

    payments: '/finance/payments',
    payment: (id: string) => `/finance/payments/${id}`,
    paymentReimbursements: (paymentId: string) => `/finance/payments/${paymentId}/reimbursements`,
    reimbursementVoid: (paymentId: string, reimbursementId: string) =>
      `/finance/payments/${paymentId}/reimbursements/${reimbursementId}/void`,
    reimbursements: '/finance/reimbursements',

    assets: '/finance/assets',
    assetsExport: '/finance/assets/export',
    asset: (id: string) => `/finance/assets/${id}`,
    assetAssign: (id: string) => `/finance/assets/${id}/assign`,
    assetReturn: (id: string) => `/finance/assets/${id}/return`,
    assetTransfer: (id: string) => `/finance/assets/${id}/transfer`,
    assetDispose: (id: string) => `/finance/assets/${id}/dispose`,
    assetCredentials: (id: string) => `/finance/assets/${id}/credentials`,
    assetCredential: (id: string, credentialId: string) =>
      `/finance/assets/${id}/credentials/${credentialId}`,
    assetCredentialReveal: (id: string, credentialId: string) =>
      `/finance/assets/${id}/credentials/${credentialId}/reveal`,
    assetCredentialsExport: '/finance/asset-credentials/export',
    assetAssignments: '/finance/asset-assignments',
    assetDisposals: '/finance/asset-disposals',
    assetRepairs: '/finance/asset-repairs',
    assetRepair: (id: string) => `/finance/asset-repairs/${id}`,
    assetRepairCancel: (id: string) => `/finance/asset-repairs/${id}/cancel`,
    assetRepairComplete: (id: string) => `/finance/asset-repairs/${id}/complete`,

    lookups: {
      assetCategories: '/finance/lookups/asset-categories',
      billingFrequencies: '/finance/lookups/billing-frequencies',
      companies: '/finance/lookups/companies',
      employees: '/finance/lookups/employees',
      locations: '/finance/lookups/locations',
      paymentAccounts: '/finance/lookups/payment-accounts',
      paymentCards: '/finance/lookups/payment-cards',
    },
  },

  /** Finance's own files (company/finance documents, invoice attachments, proofs). */
  files: {
    list: '/files',
    upload: '/files/upload',
    file: (fileId: string) => `/files/${fileId}`,
    download: (fileId: string) => `/files/${fileId}/download`,
    restore: (fileId: string) => `/files/${fileId}/restore`,
  },
} as const;
